import * as React from 'react'
import { type FC, useEffect } from 'react'
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Input,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import { useAlarmTypeSelectOptions } from '../AlarmTypeSelectOptions'
import { type CompanyAlarm } from '../../../../redux/companies/companies-types'
import { SetCompanyAlarmAction } from '../../../../redux/meter-table/meter-table-actions'
import { type AlarmKey, FLOW_LIMIT, getAlarmTypeByKey } from '../../../../redux/alarm-settings/alarm-types'
import { useAppDispatch } from '../../../../redux/hooks'
import { useTranslation } from 'react-i18next'

interface CompanyData {
  id: string
  name: string
}

interface SetCompanyAlarmFormProps {
  companies: CompanyData[]
  companyAlarm: CompanyAlarm
}

export const SetCompanyAlarmForm: FC<SetCompanyAlarmFormProps> = ({
  companies,
  companyAlarm
}) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const alarmTypeSelectOptions = useAlarmTypeSelectOptions()
  const [isUpdateFlowLimitEnabled, setIsUpdateFlowLimitEnabled] = React.useState(false)

  const firstCompanyId = companies.length > 0 ? companies[0].id : null

  useEffect(() => {
    if (companyAlarm.companyId == null) {
      dispatch(
        SetCompanyAlarmAction({
          companyId: firstCompanyId,
          alarmType: getAlarmTypeByKey(alarmTypeSelectOptions[0].value),
          state: false,
          maxFlowPerHour: null
        })
      )
    }
  }, [firstCompanyId, companyAlarm, dispatch, alarmTypeSelectOptions])

  const handleCompanyChange = (event: SelectChangeEvent<string>): void => {
    const company = companies.find(
      (company) => company.id === event.target.value
    )
    if (company != null) {
      dispatch(
        SetCompanyAlarmAction({ ...companyAlarm, companyId: company.id })
      )
    }
  }

  const handleAlarmStateChange = (
    _event: React.MouseEvent<HTMLElement>,
    newState: string
  ): void => {
    dispatch(
      SetCompanyAlarmAction({ ...companyAlarm, state: newState === 'true' })
    )
  }

  const handleAlarmTypeChange = (event: SelectChangeEvent<AlarmKey>): void => {
    dispatch(
      SetCompanyAlarmAction({ ...companyAlarm, alarmType: getAlarmTypeByKey(event.target.value) })
    )
  }

  const handleMaxFlowPerHourChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isUpdateFlowLimitEnabled) {
      dispatch(
        SetCompanyAlarmAction({
          ...companyAlarm,
          maxFlowPerHour: event.target.value === '' ? null : parseInt(event.target.value)
        })
      )
    }
  }

  const handleIsUpdateFlowLimitEnabledChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setIsUpdateFlowLimitEnabled(event.target.checked)
    dispatch(
      SetCompanyAlarmAction({ ...companyAlarm, maxFlowPerHour: null })
    )
  }

  const getCompanySelect = (): React.ReactElement => {
    if (companies.length === 1) {
      return (
                <Input
                    data-testid="single-company-input"
                    type="hidden"
                    value={companies[0]}
                />
      )
    }
    return (
            <FormControl fullWidth margin="dense">
                <FormLabel id="company-select-label">
                    {t('classifications.company')}
                </FormLabel>
                <Select
                    style={{ width: '30rem' }}
                    labelId="company-select-label"
                    id="company-select"
                    data-testid="company-select"
                    value={
                        companyAlarm.companyId ??
                        (companies.length > 0 ? companies[0].id : '')
                    }
                    onChange={handleCompanyChange}
                >
                    {companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                            {company.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
    )
  }

  const getMaxFlowPerHourInput = (): React.ReactElement | null => {
    if (companyAlarm.alarmType !== FLOW_LIMIT) {
      return null
    }

    return (
            <FormControl margin="dense">
                <FormLabel id="max-flow-per-hour-label">
                    {t('company_alarm_dialog.max_flow_per_hour')}
                </FormLabel>
                {isUpdateFlowLimitEnabled && (
                    <TextField
                        variant="outlined"
                        data-testid="max-flow-per-hour-input"
                        type="number"
                        size="small"
                        value={companyAlarm.maxFlowPerHour ?? ''}
                        onChange={handleMaxFlowPerHourChange}
                    />
                )}
                <FormControlLabel
                    control={<Checkbox onChange={handleIsUpdateFlowLimitEnabledChange}/>}
                    label={t('company_alarm_dialog.update_flow_limit_rate')}
                    data-testid="update-flow-limit-rate-checkbox"
                />
            </FormControl>
    )
  }

  return (
        <>
            {getCompanySelect()}
            <FormControl
                fullWidth
                margin="dense"
                data-testid="set-company-alarm-form"
            >
                <FormLabel id="alarm-select-label">
                    {t('company_alarm_dialog.alarm_types')}
                </FormLabel>
                <Select
                    style={{ width: '30rem' }}
                    labelId="alarm-select-label"
                    id="alarm-select"
                    value={companyAlarm.alarmType?.alarmKey ?? alarmTypeSelectOptions[0].value as AlarmKey}
                    onChange={handleAlarmTypeChange}
                    data-testid="alarm-type-select"
                >
                    {alarmTypeSelectOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {getMaxFlowPerHourInput()}
            <FormControl fullWidth margin="dense">
                <FormLabel id="alarm-select-label">
                    {t('company_alarm_dialog.alarm_state')}
                </FormLabel>
                <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={companyAlarm.state.toString()}
                    exclusive
                    onChange={handleAlarmStateChange}
                    data-testid="alarm-state"
                >
                    <ToggleButton value="true" data-testid="on-button">
                        {t('company_alarm_dialog.on')}
                    </ToggleButton>
                    <ToggleButton value="false" data-testid="off-button">
                        {t('company_alarm_dialog.off')}
                    </ToggleButton>
                </ToggleButtonGroup>
            </FormControl>
        </>
  )
}
